import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { FollowUpRequestBoardDetailModal } from '@/follow-up/FollowUpRequestBoardDetailModal';
import CoordinatorPendingRequestsTable from '@/tables/CoordinatorPendingRequestsTable';
import PartnerPendingFollowUpsTable from '@/tables/PartnerPendingFollowUpsTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-ups/pending-requests/')({
  component: PendingRequestsScreen,
});

/**
 * The Pending Requests subnav of both follow-up portals, which are different
 * tables.
 *
 * The coordinator's lists requests they've claimed from the board and not yet
 * converted; the partner's lists follow-up invitations they haven't answered,
 * whatever the follow-up's status. The two have different headings too.
 */
function PendingRequestsScreen() {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();
  const [detailRequestId, setDetailRequestId] = useState<number | null>(null);

  const isCoordinator = me?.profile_type === 'FOLLOWUP_COORDINATOR';
  const isPartner = me?.profile_type === 'FOLLOWUP_PARTNER';

  // No admin arm, unlike the board: both list endpoints scope on the caller's
  // own coordinator or partner row, which an admin doesn't have. `isPending`
  // matters: a bare `!canView` bounces everyone while `/users/me` loads.
  if (isPending) return null;
  if (!isCoordinator && !isPartner) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* The heading and help placement both depend on the caller's role. */}
        <TitleWithQuestion
          title={
            isCoordinator
              ? t('lessons.student-waiting-for-follow-up')
              : t('wbs.pending-requests')
          }
          placement={
            isCoordinator
              ? 'follow_up_for_coordinators'
              : isPartner
                ? 'follow_up_for_partners'
                : undefined
          }
        />
        {isCoordinator && (
          // The only header control either portal has.
          <WBSButton as={Link} to="/follow-up-request-board" wide>
            {t('wbs.go-to-request-board')}
          </WBSButton>
        )}
      </GroupHeaderWrapper>
      {isCoordinator ? (
        <>
          <CoordinatorPendingRequestsTable onViewDetail={setDetailRequestId} />
          {/* The same popup the board uses. The endpoint also serves
             requests the caller has claimed, which is what makes it work
             here: these rows are claimed, so they're off the board. */}
          <FollowUpRequestBoardDetailModal
            requestId={detailRequestId}
            onClose={() => setDetailRequestId(null)}
          />
        </>
      ) : (
        <PartnerPendingFollowUpsTable />
      )}
    </PageContainer>
  );
}
